import { Component, computed, ElementRef, HostListener, inject, Input, OnDestroy, QueryList, ViewChild, ViewChildren } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { filter, map, Subscription } from 'rxjs';
import { NavItem } from '@shared/interfaces/nav/nav-item';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { ChevronLeft, Copy, LucideAngularModule, Menu, X } from 'lucide-angular';
import { MenuAction } from '@shared/interfaces/header/menu-action';
import { TranslatePipe } from '@ngx-translate/core';
import { NgClass } from '@angular/common';
import { HeaderService } from '@app/core/services/header.service';
import { IsEmptyDirective } from '@shared/directives/is-empty/is-empty.directive';
import { AlertServiceWrapperComponent } from '@shared/components/alert-service-wrapper/alert-service-wrapper.component';
import { NavigationService } from '@app/core/services/navigation.service';
import { copyToClipboard } from '@app/core/utils/copy-to-clipboard';
import { ToastService } from '@app/core/services/toast.service';
import { PreferencesService } from '@app/core/services/preferences.service';
import { HotkeyService } from '@app/core/services/hotkey.service';
import { menu } from '@app/core/constants/menu';

/**
 * HeaderComponent handles navigation, search toggle, and contextual menu actions.
 * It also responds to escape key and click events to manage the action menu.
 * Hotkeys for opening the menu and per-action shortcuts are registered via HotkeyService.
 */
@Component({
  selector: 'app-header',
  imports: [
    RouterLink,
    RouterLinkActive,
    LucideAngularModule,
    TranslatePipe,
    NgClass,
    IsEmptyDirective,
    AlertServiceWrapperComponent,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.css'
})
export class HeaderComponent implements OnDestroy {
  /**
   * Icons
   */
  protected readonly ChevronLeft = ChevronLeft;
  protected readonly X = X;
  protected readonly Menu = Menu;
  protected readonly Copy = Copy;

  /**
   * Reference to the action menu DOM element, used to detect outside clicks.
   */
  @ViewChild('actionMenu', { static: false }) public actionMenu!: ElementRef;

  /**
   * References to all focusable anchor elements inside the action menu.
   * Used for keyboard navigation (arrow keys / Tab).
   */
  @ViewChildren('menuItem') public menuItems!: QueryList<ElementRef<HTMLAnchorElement>>;

  /**
   * Listens for the Escape key to close the action menu if it's open.
   * @param event - The keyboard event.
   */
  @HostListener('document:keydown', ['$event'])
  public onKeyDown(event: Event): void {
    if (event instanceof KeyboardEvent) {
      if (event.key === 'Escape') {
        if (this.actionsOpen) {
          this.actionsOpen = false;
        }
      }
    }
  }

  /**
   * Listens for clicks outside the action menu to close it.
   * @param event - The mouse click event.
   */
  @HostListener('document:click', ['$event'])
  public clickOutside(event: Event) {
    if (event instanceof MouseEvent) {
      if (this.actionsOpen && this.actionMenu && !this.actionMenu.nativeElement.contains(event.target)) {
        this.actionsOpen = false;
      }
    }
  }

  /**
   * Navigation links to display in the header.
   */
  @Input() public navItems?: NavItem[];

  /**
   * Enables the search feature in the header when true.
   */
  @Input() public search: boolean = false;

  /**
   * Shows History Back Button
   **/
  @Input() public enableBack: boolean = true;

  /**
   * Whether the actions menu is currently open. All writes go through the
   * HeaderService so other components (e.g. tables doing keyboard navigation)
   * can react to the menu opening or closing.
   */
  private _actionsOpen: boolean = false;
  public get actionsOpen(): boolean {
    return this._actionsOpen;
  }
  public set actionsOpen(open: boolean) {
    this._actionsOpen = open;
    this.headerService.setActionsOpen(open);
  }

  /** Tracks which action hotkey IDs are currently registered so they can be cleaned up. */
  private readonly registeredActionHotkeyIds = new Set<string>();

  /** Subscription to menu signal changes for reactive hotkey sync. */
  private readonly menuSubscription: Subscription;

  public readonly headerService = inject(HeaderService);
  public readonly navigationService = inject(NavigationService);
  public readonly preferencesService = inject(PreferencesService);
  public readonly hotkeyService = inject(HotkeyService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);

  /**
   * Current router URL, updated after each successful navigation.
   */
  private readonly currentUrl = toSignal(
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      map(e => e.urlAfterRedirects),
    ),
    { initialValue: this.router.url },
  );

  /**
   * The menu element whose route matches the current URL, if any.
   * Used to show the active page's icon in the header.
   */
  public readonly activeMenuItem = computed(() => {
    const url = this.currentUrl();
    return menu
      .flatMap(section => section.items)
      .find(item => !!item.route && (url === `/${item.route}` || url.startsWith(`/${item.route}/`)));
  });

  constructor() {
    // Reactively sync per-action hotkeys whenever the menu signal changes.
    // toObservable converts the signal to an Observable so we get a reliable
    // stream of menu updates — including ones that happen after init.
    this.menuSubscription = toObservable(this.headerService.menu).subscribe(menu => {
      // Unregister all previously registered menu hotkeys (including menu-open)
      for (const id of this.registeredActionHotkeyIds) {
        this.hotkeyService.unregister(id);
      }
      this.registeredActionHotkeyIds.clear();

      if (menu.length === 0) return;

      // Only register the menu-open hotkey when there is actually a menu
      this.hotkeyService.register({
        id: 'header-menu-open',
        keys: 'shift',
        doubleTap: true,
        label: 'hotkeys.label.openMenu',
        group: 'hotkeys.group.page',
        callback: () => this.openMenuWithFocus(),
      });
      this.registeredActionHotkeyIds.add('header-menu-open');

      // Register hotkeys for individual actions that declare one
      for (const action of menu) {
        if (action.hotkey && action.callback) {
          const hotkeyId = `header-action-${action.id}`;
          this.hotkeyService.register({
            id: hotkeyId,
            keys: action.hotkey,
            label: action.title,
            group: 'hotkeys.group.page',
            callback: () => {
              if (!this.isDisabled(action)) {
                action.callback!();
              }
            },
          });
          this.registeredActionHotkeyIds.add(hotkeyId);
        }
      }
    });
  }

  public ngOnDestroy(): void {
    this.menuSubscription.unsubscribe();
    for (const id of this.registeredActionHotkeyIds) {
      this.hotkeyService.unregister(id);
    }
    this.registeredActionHotkeyIds.clear();
  }

  /**
   * Returns only the menu item elements that correspond to non-disabled actions,
   * used to restrict keyboard focus navigation to interactive items only.
   */
  private getFocusableMenuItems(): ElementRef<HTMLAnchorElement>[] {
    const menu = this.headerService.menu();
    return (this.menuItems?.toArray() ?? []).filter((_, i) => !this.isDisabled(menu[i]));
  }

  /**
   * Opens or closes the action menu. When opening, focuses the first non-disabled item.
   */
  public openMenuWithFocus(): void {
    this.actionsOpen = !this.actionsOpen;
    if (this.actionsOpen) {
      // Wait one tick for the menu DOM to render before focusing
      setTimeout(() => {
        const focusable = this.getFocusableMenuItems();
        if (focusable.length > 0) {
          focusable[0].nativeElement.focus();
        }
      });
    }
  }

  /**
   * Handles arrow-key and Tab navigation within the open action menu,
   * skipping over disabled items.
   */
  public onMenuKeydown(event: KeyboardEvent, index: number): void {
    const allItems = this.menuItems?.toArray() ?? [];
    const menu = this.headerService.menu();
    if (allItems.length === 0) return;

    // Build a list of focusable (non-disabled) indices within allItems
    const focusableIndices = allItems
      .map((_, i) => i)
      .filter(i => !this.isDisabled(menu[i]));

    if (focusableIndices.length === 0) return;

    const currentPos = focusableIndices.indexOf(index);

    if (event.key === 'ArrowDown' || (event.key === 'Tab' && !event.shiftKey)) {
      event.preventDefault();
      const next = focusableIndices[(currentPos + 1) % focusableIndices.length];
      allItems[next].nativeElement.focus();
    } else if (event.key === 'ArrowUp' || (event.key === 'Tab' && event.shiftKey)) {
      event.preventDefault();
      const prev = focusableIndices[(currentPos - 1 + focusableIndices.length) % focusableIndices.length];
      allItems[prev].nativeElement.focus();
    }
  }

  /**
   * Executes a menu action's callback and closes the menu.
   * @param item - The menu action to trigger.
   */
  public handleAction(item: MenuAction): void {
    if (item?.callback) {
      item.callback();
      this.actionsOpen = false;
    }
  }

  /**
   * Evaluates whether a given action is disabled.
   * @param action - The menu action to check.
   * @returns True if the action is disabled, false otherwise.
   */
  public isDisabled(action: MenuAction): boolean {
    return typeof action.disabled === 'function'
      ? action.disabled()
      : !!action.disabled;
  }

  public copyRessource(text: string): void {
    copyToClipboard(text).then(() => {
      this.toastService.show('common.copytext.success')
    });
  }

}
